import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BackPill } from './BackPill';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Feather } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BACKEND_URL } from '../../utils/api';

/* ═══════════════════════════════════════════════════════════════════
   SOVEREIGNS PATH — single hardback book with 8 chapters
   • Page 0: Leather cover (oxblood + gold dragon, "SOVEREIGNS PATH")
   • Page 1: Chapter index — 8 colored chapter cards
   • When a chapter is opened, pages 2..N are that category's images,
     one image per page, with date/time + a "passage" textarea the
     sovereign can write into. PATCH /api/memories/{id} {note}.
   ═══════════════════════════════════════════════════════════════════ */

const GOLD       = '#D4AF37';
const GOLD_LIGHT = '#F5D97A';
const INK        = '#1a1208';

/* ── Chapter palette — each category has its own royal colorway ── */
const CHAPTERS = [
  { id: 'fashion', label: 'Fashion', accent: '#D4AF37', icon: '/icons/leopard_v2.png',  bg: '#332608' },
  { id: 'food',    label: 'Taste',   accent: '#E8D88C', icon: '/icons/gazelle.png',     bg: '#2c2410' },
  { id: 'explore', label: 'Explore', accent: '#0F52BA', icon: '/icons/falcon_v2.png',   bg: '#0c1d3c' },
  { id: 'souls',   label: 'Soul',    accent: '#DC143C', icon: '/icons/phoenix_v2.png',  bg: '#3a0a14' },
  { id: 'sporty',  label: 'Sport',   accent: '#10B981', icon: '/icons/gorilla_v2.png',  bg: '#0a2a20' },
  { id: 'success', label: 'Success', accent: '#C0C0C0', icon: '/icons/stallion_v2.png', bg: '#1f1f24' },
  { id: 'legacy',  label: 'Legacy',  accent: '#8B5CF6', icon: '/icons/lion_legacy.png', bg: '#221534' },
  { id: 'art',     label: 'Art',     accent: '#1E6FE3', icon: '/icons/crystal_crane.png', bg: '#0a1530' },
];

/* ── Reusable parchment paper page chrome ── */
const PaperPage = ({ children, side = 'right' }) => (
  <div
    className="absolute inset-0 flex flex-col"
    style={{
      background: 'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 50%, #d8c084 100%)',
      borderRadius: side === 'right' ? '4px 14px 14px 4px' : '14px 4px 4px 14px',
      boxShadow:
        'inset 0 0 70px rgba(168,107,31,0.18), inset 0 0 12px rgba(82,46,8,0.5), 0 8px 28px rgba(0,0,0,0.6)',
      overflow: 'hidden',
    }}
  >
    <div className="absolute inset-2 pointer-events-none" style={{ border: `2px solid ${GOLD}`, borderRadius: 6 }} />
    <div className="absolute inset-3.5 pointer-events-none" style={{ border: '1px solid rgba(168,107,31,0.5)', borderRadius: 4 }} />
    {['1.5rem', 'calc(100% - 1.5rem)'].map((y) =>
      ['1.5rem', 'calc(100% - 1.5rem)'].map((x) => (
        <span
          key={`${x}-${y}`}
          aria-hidden
          style={{
            position: 'absolute', left: x, top: y,
            width: 14, height: 14,
            transform: 'translate(-50%, -50%) rotate(45deg)',
            border: `1px solid ${GOLD}`,
            background: 'rgba(212,175,55,0.18)',
            pointerEvents: 'none',
          }}
        />
      ))
    )}
    <div
      aria-hidden
      style={{
        position: 'absolute', top: 0, bottom: 0,
        [side === 'right' ? 'left' : 'right']: 0,
        width: 16,
        background: side === 'right'
          ? 'linear-gradient(90deg, rgba(0,0,0,0.45), transparent)'
          : 'linear-gradient(270deg, rgba(0,0,0,0.45), transparent)',
        pointerEvents: 'none',
      }}
    />
    {children}
  </div>
);

/* ── MASTER COVER — royal purple "Sovereign's Path" volume cover ──
   The first page when the user opens Sovereign's Path. Tap ▶ to enter
   the Library shelf of 8 chronicle books. */
const MasterCoverPage = ({ empireName, profilePicture }) => {
  const initial = (empireName || 'A')[0].toUpperCase();
  const PURPLE_DEEP = '#3a1a6e';
  const PURPLE_MID  = '#2a0f4a';
  const PURPLE_DARK = '#120628';
  const PURPLE_GLOW = '#8B5CF6';
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 overflow-hidden"
      style={{
        background: `radial-gradient(ellipse at 50% 35%, ${PURPLE_DEEP} 0%, ${PURPLE_MID} 50%, ${PURPLE_DARK} 100%)`,
        borderRadius: '4px 14px 14px 4px',
        boxShadow: 'inset 0 0 90px rgba(0,0,0,0.55), inset 0 0 18px rgba(0,0,0,0.7), 0 12px 32px rgba(0,0,0,0.7)',
      }}
    >
      {/* Velvet noise grain */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background:
          'radial-gradient(circle at 20% 22%, rgba(255,255,255,0.05) 0%, transparent 30%),' +
          'radial-gradient(circle at 78% 28%, rgba(0,0,0,0.2) 0%, transparent 28%),' +
          'radial-gradient(circle at 30% 78%, rgba(0,0,0,0.22) 0%, transparent 30%),' +
          'radial-gradient(circle at 82% 76%, rgba(180,140,255,0.08) 0%, transparent 32%)',
        mixBlendMode: 'overlay', opacity: 0.9,
      }} />
      {/* Vignette */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)',
      }} />
      {/* Embossed gold frame stack */}
      <div aria-hidden className="absolute inset-3 pointer-events-none" style={{
        border: `2.5px solid ${GOLD}`, borderRadius: 8,
        boxShadow: `inset 0 0 0 1px rgba(255,241,184,0.4), 0 0 14px ${GOLD}55`,
      }} />
      <div aria-hidden className="absolute inset-5 pointer-events-none" style={{
        border: `1px solid ${GOLD}88`, borderRadius: 5,
      }} />
      {/* Corner medallions */}
      {[{top:14,left:14},{top:14,right:14},{bottom:14,left:14},{bottom:14,right:14}].map((pos, i) => (
        <div key={i} aria-hidden className="absolute pointer-events-none" style={{
          ...pos, width: 22, height: 22, transform: 'rotate(45deg)',
          background: 'linear-gradient(135deg, #F5E6A0 0%, #D4AF37 45%, #8B6B1F 100%)',
          border: '1px solid rgba(60,40,8,0.6)',
          boxShadow: `0 0 8px ${GOLD}88, inset 0 0 4px rgba(255,255,255,0.35)`,
        }} />
      ))}
      {/* Dragon-seal watermark */}
      <img src="/icons/empire_seal.png" alt="" aria-hidden draggable={false}
        className="absolute pointer-events-none select-none"
        style={{ width: '70%', height: 'auto', left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)', opacity: 0.10,
          filter: 'sepia(1) hue-rotate(-15deg) saturate(2) brightness(1.4)',
          mixBlendMode: 'screen' }} />
      {/* Spine shadow */}
      <div aria-hidden style={{ position:'absolute', top:0, bottom:0, right:0, width:18,
        background: 'linear-gradient(270deg, rgba(0,0,0,0.6), transparent)', pointerEvents: 'none' }} />

      <p className="font-['Cormorant_Garamond'] uppercase tracking-[0.42em] text-[11px] mb-6 relative z-10"
        style={{ color: '#EFD9FF', textShadow: '0 1px 0 rgba(0,0,0,0.5)' }}>
        ~ Chronicle of an Empire ~
      </p>

      {/* Profile medallion with purple halo */}
      <div className="relative mb-6 z-10" style={{ width: 120, height: 120 }}>
        <div aria-hidden className="absolute inset-0 rounded-full" style={{
          background: `radial-gradient(circle, ${PURPLE_GLOW}aa 0%, transparent 65%)`,
          filter: 'blur(12px)', transform: 'scale(1.4)',
        }} />
        <div className="absolute inset-0 rounded-full overflow-hidden" style={{
          border: '3px solid #D4AF37',
          boxShadow: 'inset 0 0 0 2px rgba(255,241,184,0.7), 0 0 20px rgba(212,175,55,0.55), 0 6px 18px rgba(0,0,0,0.55)',
          background: profilePicture ? '#1a0628'
            : 'radial-gradient(circle at 50% 32%, #f6e6b6, #c69b3f 70%, #6b4612 100%)',
        }}>
          {profilePicture ? (
            <img src={profilePicture} alt={empireName} draggable={false}
              style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center" style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 700,
              fontSize: 54, fontStyle: 'italic', color: '#3a1a4a',
              textShadow: '0 1px 0 rgba(255,241,184,0.6)',
            }}>{initial}</div>
          )}
        </div>
      </div>

      {/* Title — embossed gold-foil */}
      <h1 className="font-['Cormorant_Garamond'] font-bold leading-tight relative z-10"
        style={{
          fontSize: 'clamp(34px, 9.5vw, 54px)', letterSpacing: '0.06em',
          background: 'linear-gradient(180deg, #FFF1B8 0%, #F5D97A 35%, #C69B3F 75%, #7A5418 100%)',
          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
          textShadow: `0 2px 0 rgba(0,0,0,0.4), 0 0 22px ${PURPLE_GLOW}55`,
        }}>
        SOVEREIGN&apos;S<br />PATH
      </h1>

      <div className="my-5 w-32 h-[1.5px] relative z-10" style={{
        background: `linear-gradient(90deg, transparent, ${GOLD_LIGHT}, transparent)`,
        boxShadow: `0 0 8px ${GOLD_LIGHT}66`,
      }} />

      <h2 className="font-['Cormorant_Garamond'] italic font-light relative z-10" style={{
        color: '#EFD9FF', fontSize: 'clamp(20px, 5.5vw, 28px)', letterSpacing: '0.08em',
        textShadow: '0 1px 0 rgba(0,0,0,0.5)',
      }}>by {empireName}</h2>

      <div className="mt-8 opacity-80 relative z-10">
        <Feather size={32} style={{ color: GOLD_LIGHT, filter: `drop-shadow(0 0 6px ${PURPLE_GLOW})` }} />
      </div>
      <p className="absolute bottom-7 left-0 right-0 text-center font-['Cormorant_Garamond'] italic text-[11px] z-10"
        style={{ color: 'rgba(239,217,255,0.7)' }}>
        — turn the page to see your latest —
      </p>
    </div>
  );
};

/* ── PILLAR MASTER COVER — per-pillar volume cover.  Same royal luxury as
   the master cover but tinted by the pillar's palette, with the pillar
   icon repeated in the four gold corner medallions.  Used when the book
   is opened from a specific pillar tab (Fashion / Taste / Explore / …). */
const PillarMasterCover = ({ empireName, profilePicture, pillar }) => {
  const initial = (empireName || 'A')[0].toUpperCase();
  const accent  = pillar?.accent || GOLD;
  const bg      = pillar?.bg     || '#2a1608';
  const glow    = `${accent}aa`;
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 overflow-hidden"
      style={{
        background: `radial-gradient(ellipse at 50% 35%, ${accent}22 0%, ${bg} 55%, #06030a 100%)`,
        borderRadius: '4px 14px 14px 4px',
        boxShadow: 'inset 0 0 90px rgba(0,0,0,0.55), inset 0 0 18px rgba(0,0,0,0.7), 0 12px 32px rgba(0,0,0,0.7)',
      }}
    >
      {/* Vignette */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)',
      }} />
      {/* Gold frame stack */}
      <div aria-hidden className="absolute inset-3 pointer-events-none" style={{
        border: `2.5px solid ${accent}`, borderRadius: 8,
        boxShadow: `inset 0 0 0 1px rgba(255,241,184,0.35), 0 0 14px ${accent}55`,
      }} />
      <div aria-hidden className="absolute inset-5 pointer-events-none" style={{
        border: `1px solid ${accent}88`, borderRadius: 5,
      }} />
      {/* Four corner icons REMOVED per user request (Feb 2026).  The
          pillar's animal icon now sits at the bottom of the cover in
          place of the feather. */}

      {/* Chapter-of-the-empire tagline */}
      <p className="font-['Cormorant_Garamond'] uppercase tracking-[0.42em] text-[11px] mb-6 relative z-10"
        style={{ color: accent, textShadow: '0 1px 0 rgba(0,0,0,0.5)' }}>
        ~ Chronicle of {pillar?.label || 'the Realm'} ~
      </p>

      {/* Profile medallion */}
      <div className="relative mb-6 z-10" style={{ width: 120, height: 120 }}>
        <div aria-hidden className="absolute inset-0 rounded-full" style={{
          background: `radial-gradient(circle, ${glow} 0%, transparent 65%)`,
          filter: 'blur(12px)', transform: 'scale(1.4)',
        }} />
        <div className="absolute inset-0 rounded-full overflow-hidden" style={{
          border: `3px solid ${accent}`,
          boxShadow: `inset 0 0 0 2px rgba(255,241,184,0.5), 0 0 20px ${accent}88, 0 6px 18px rgba(0,0,0,0.55)`,
          background: profilePicture ? '#1a0628' : `radial-gradient(circle at 50% 32%, ${accent}, ${bg} 70%, #06030a 100%)`,
        }}>
          {profilePicture ? (
            <img src={profilePicture} alt={empireName} draggable={false}
              style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center" style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 700,
              fontSize: 54, fontStyle: 'italic', color: '#3a1a4a',
              textShadow: '0 1px 0 rgba(255,241,184,0.6)',
            }}>{initial}</div>
          )}
        </div>
      </div>

      {/* Title — pillar-colour foil */}
      <h1 className="font-['Cormorant_Garamond'] font-bold leading-tight relative z-10"
        style={{
          fontSize: 'clamp(30px, 8.5vw, 48px)', letterSpacing: '0.06em',
          background: `linear-gradient(180deg, #FFF1B8 0%, ${accent} 55%, #7A5418 100%)`,
          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
          textShadow: `0 2px 0 rgba(0,0,0,0.4), 0 0 22px ${accent}66`,
        }}>
        {(pillar?.label || 'Sovereign').toUpperCase()}<br/>PATH
      </h1>

      <div className="my-5 w-32 h-[1.5px] relative z-10" style={{
        background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
        boxShadow: `0 0 8px ${accent}66`,
      }} />

      <h2 className="font-['Cormorant_Garamond'] italic font-light relative z-10" style={{
        color: '#F5E6A0', fontSize: 'clamp(20px, 5.5vw, 26px)', letterSpacing: '0.08em',
        textShadow: '0 1px 0 rgba(0,0,0,0.5)',
      }}>by {empireName}</h2>

      {/* Signature pillar mark — replaces the feather.  Circular gold-ring
          badge hosting the pillar's animal icon, glowing in the pillar's
          accent colour. */}
      {pillar?.icon && (
        <div className="mt-7 relative z-10 rounded-full overflow-hidden"
          style={{
            width: 54, height: 54,
            border: `1.5px solid ${accent}`,
            boxShadow: `0 0 14px ${glow}, inset 0 0 6px rgba(255,255,255,0.15)`,
            background: `radial-gradient(circle at 30% 25%, ${accent}44, #05050a 80%)`,
          }}
          data-testid={`pillar-signature-${pillar?.id || 'x'}`}>
          <img src={pillar.icon} alt={pillar.label || ''} draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}
      <p className="absolute bottom-7 left-0 right-0 text-center font-['Cormorant_Garamond'] italic text-[11px] z-10"
        style={{ color: 'rgba(255,241,184,0.75)' }}>
        — turn the page to see your latest —
      </p>
    </div>
  );
};

/* ── Per-chapter Cover — full-screen oxblood leather book cover ──
   Tinted with the chapter's accent color, prominent chapter icon
   (user profile is hidden here — focus stays on the chronicle). */
const ChapterCoverPage = ({ chapter, empireName }) => {
  const accent = chapter?.accent || GOLD;
  const bg = chapter?.bg || '#2a1608';
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 overflow-hidden"
      style={{
        background: `radial-gradient(ellipse at 50% 38%, ${bg} 0%, #1a0d04 55%, #050306 100%)`,
        borderRadius: '4px 14px 14px 4px',
        boxShadow: 'inset 0 0 90px rgba(0,0,0,0.55), inset 0 0 18px rgba(0,0,0,0.7), 0 12px 32px rgba(0,0,0,0.7)',
      }}
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background:
          'radial-gradient(circle at 18% 22%, rgba(255,255,255,0.04) 0%, transparent 30%),' +
          'radial-gradient(circle at 78% 28%, rgba(0,0,0,0.18) 0%, transparent 28%),' +
          'radial-gradient(circle at 30% 78%, rgba(0,0,0,0.22) 0%, transparent 30%),' +
          'radial-gradient(circle at 82% 76%, rgba(255,225,180,0.05) 0%, transparent 32%)',
        mixBlendMode: 'overlay', opacity: 0.85,
      }} />
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.5) 100%)',
      }} />
      <div aria-hidden className="absolute inset-3 pointer-events-none" style={{
        border: `2.5px solid ${accent}`, borderRadius: 8,
        boxShadow: `inset 0 0 0 1px ${accent}66, 0 0 14px ${accent}66`,
      }} />
      <div aria-hidden className="absolute inset-5 pointer-events-none" style={{
        border: `1px solid ${accent}88`, borderRadius: 5,
      }} />
      {[{top:14,left:14},{top:14,right:14},{bottom:14,left:14},{bottom:14,right:14}].map((pos, i) => (
        <div key={i} aria-hidden className="absolute pointer-events-none" style={{
          ...pos, width: 22, height: 22, transform: 'rotate(45deg)',
          background: `linear-gradient(135deg, ${accent} 0%, #8B6B1F 100%)`,
          border: '1px solid rgba(60,40,8,0.6)',
          boxShadow: `0 0 8px ${accent}88, inset 0 0 4px rgba(255,255,255,0.35)`,
        }} />
      ))}
      <div aria-hidden style={{ position:'absolute', top:0, bottom:0, right:0, width:18,
        background: 'linear-gradient(270deg, rgba(0,0,0,0.6), transparent)', pointerEvents: 'none' }} />

      <p className="font-['Cormorant_Garamond'] uppercase tracking-[0.42em] text-[11px] mb-6 relative z-10"
        style={{ color: '#F5E6A0', textShadow: '0 1px 0 rgba(0,0,0,0.45)' }}>
        ~ Chronicle of {chapter?.label} ~
      </p>

      {/* Large chapter icon — the only centerpiece (user profile hidden) */}
      <div className="relative mb-6 z-10" style={{ width: 170, height: 170 }}>
        <div aria-hidden className="absolute inset-0 rounded-full" style={{
          background: `radial-gradient(circle, ${accent}88 0%, transparent 70%)`,
          filter: 'blur(16px)', transform: 'scale(1.4)',
        }} />
        <div className="absolute inset-0 rounded-full overflow-hidden flex items-center justify-center" style={{
          border: `3px solid ${accent}`,
          boxShadow: `inset 0 0 0 2px rgba(255,241,184,0.4), 0 0 22px ${accent}aa, 0 6px 18px rgba(0,0,0,0.55)`,
          background: 'radial-gradient(circle at 50% 32%, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.85) 100%)',
        }}>
          <img src={chapter?.icon} alt={chapter?.label} draggable={false}
            style={{ width: '78%', height: '78%', objectFit: 'contain',
              mixBlendMode: 'lighten' }} />
        </div>
      </div>

      <h1 className="font-['Cormorant_Garamond'] font-bold leading-tight relative z-10"
        style={{
          fontSize: 'clamp(36px, 10vw, 52px)', letterSpacing: '0.06em',
          background: `linear-gradient(180deg, #FFF1B8 0%, ${accent} 50%, #7A5418 100%)`,
          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
          textShadow: `0 2px 0 rgba(0,0,0,0.4), 0 0 22px ${accent}55`,
        }}>
        {chapter?.label} Path
      </h1>

      <div className="my-4 w-32 h-[1.5px] relative z-10" style={{
        background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
        boxShadow: `0 0 8px ${accent}99`,
      }} />

      <h2 className="font-['Cormorant_Garamond'] italic font-light relative z-10" style={{
        color: '#F5E6A0', fontSize: 'clamp(16px, 4.4vw, 22px)', letterSpacing: '0.08em',
      }}>by {empireName}</h2>

      <p className="absolute bottom-7 left-0 right-0 text-center font-['Cormorant_Garamond'] italic text-[11px] z-10"
        style={{ color: 'rgba(245,230,160,0.65)' }}>
        — turn the page to enter the chronicle —
      </p>
    </div>
  );
};

/* ── Library Index — 8 horizontal index rows, all visible on one screen ──
   After tapping the purple master cover, the user lands here: a single
   page listing all 8 chronicles as full-width tabs. Tapping a row flips
   into that chapter's dedicated cover → pages. */
const LibraryShelf = ({ counts, onPick }) => (
  <div className="absolute inset-0 flex flex-col px-3 pt-2 pb-2 overflow-hidden"
    style={{
      background:
        'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.08) 0%, transparent 55%),' +
        'radial-gradient(circle at 50% 100%, rgba(30,15,4,0.6) 0%, transparent 70%),' +
        'linear-gradient(180deg, #0a0604 0%, #050306 100%)',
    }}>
    <p className="flex-shrink-0 text-center font-['Cormorant_Garamond'] uppercase tracking-[0.32em] text-[9px] pt-1 pb-2"
      style={{ color: 'rgba(245,230,160,0.55)' }}>
      ~ eight chronicles · tap any tome ~
    </p>

    {/* Single-column stack of 8 horizontal index rows. Each row fills the
        available height equally so the full list always fits on one screen. */}
    <div className="flex-1 flex flex-col gap-1.5 min-h-0">
      {CHAPTERS.map((c) => {
        const n = counts?.[c.id] || 0;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onPick(c.id)}
            data-testid={`sovereigns-book-${c.id}`}
            className="relative flex items-center flex-1 min-h-0 w-full text-left active:scale-[0.98] transition-transform"
            style={{
              background: `linear-gradient(90deg, ${c.bg} 0%, #050308 100%)`,
              border: `1.5px solid ${c.accent}`,
              borderRadius: '3px 10px 10px 3px',
              boxShadow:
                `inset 0 0 0 1px ${c.accent}55,` +
                `inset 0 0 18px rgba(0,0,0,0.55),` +
                `0 0 10px ${c.accent}55,` +
                `0 3px 8px rgba(0,0,0,0.55)`,
              padding: '4px 12px 4px 6px',
            }}
          >
            {/* Spine highlight on the left */}
            <div aria-hidden style={{
              position: 'absolute', top: 0, bottom: 0, left: 0, width: 6,
              background: `linear-gradient(180deg, ${c.accent}dd 0%, ${c.accent}66 50%, ${c.accent}cc 100%)`,
              boxShadow: 'inset -1px 0 3px rgba(0,0,0,0.75)',
            }} />

            {/* Icon medallion on the left */}
            <div className="relative flex-shrink-0 ml-2" style={{ width: 56, height: '85%', maxHeight: 56 }}>
              <div aria-hidden className="absolute inset-0 rounded-full" style={{
                background: `radial-gradient(circle, ${c.accent}55 0%, transparent 70%)`,
                filter: 'blur(8px)', transform: 'scale(1.3)',
              }} />
              <div className="absolute inset-0 rounded-full overflow-hidden flex items-center justify-center" style={{
                border: `1.5px solid ${c.accent}`,
                background: '#050308',
                boxShadow: `inset 0 0 0 1px ${c.accent}aa, 0 0 8px ${c.accent}88`,
              }}>
                <img src={c.icon} alt="" aria-hidden draggable={false}
                  style={{
                    width: '100%', height: '100%', objectFit: 'contain',
                    padding: '4px',
                    mixBlendMode: 'lighten',
                  }} />
              </div>
            </div>

            {/* Name + count + chevron — flex-1 right side */}
            <div className="flex-1 flex items-center justify-between ml-3 min-w-0">
              <div className="leading-tight min-w-0">
                <div className="font-['Cormorant_Garamond'] font-bold truncate" style={{
                  fontSize: 18, letterSpacing: '0.06em',
                  background: `linear-gradient(180deg, #FFF1B8 0%, ${c.accent} 100%)`,
                  WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
                  textShadow: '0 1px 0 rgba(0,0,0,0.5)',
                }}>
                  {c.label}
                </div>
                <div className="font-['Cormorant_Garamond'] italic mt-0.5 truncate" style={{
                  color: 'rgba(245,230,160,0.65)', fontSize: 10.5,
                }}>
                  {n === 0 ? 'no entries' : `${n} ${n === 1 ? 'entry' : 'entries'}`}
                </div>
              </div>
              <ChevronRight size={18} style={{ color: c.accent, opacity: 0.9, flexShrink: 0 }}/>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

/* ── Single image page inside a chapter ── */
const ImagePage = ({ chapter, memory, idx, total, draftNote, onChangeNote, onSave, savingNote, onBackToIndex }) => {
  const accent = chapter.accent;
  const label  = chapter.label || 'Sovereign';
  const dt = memory?.created_at ? new Date(memory.created_at) : null;
  const dateStr = dt ? dt.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '';
  return (
    <PaperPage>
      {/* Scrollable inner column — the paper keeps its full height, but if
          the passage textarea grows tall on a small screen the whole
          column becomes scrollable so the SEAL PASSAGE button is always
          reachable (fix Feb 2026). */}
      <div className="relative flex-1 flex flex-col px-4 pt-4 pb-4 overflow-y-auto no-scrollbar-book">
        {/* Header row */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            data-testid="sovereigns-back-to-index"
            onClick={onBackToIndex}
            className="font-['Cormorant_Garamond'] uppercase text-[11px] tracking-[0.28em] flex items-center gap-1"
            style={{ color: 'rgba(82,46,8,0.85)', padding: '6px 10px 6px 4px', fontWeight: 600 }}
          >
            <ChevronLeft size={16} /> Cover
          </button>
          <div className="font-['Cormorant_Garamond'] uppercase text-[10px] tracking-[0.32em]"
            style={{ color: accent }}>
            {label} Path
          </div>
          <div className="font-['Cormorant_Garamond'] italic text-[11px]"
            style={{ color: 'rgba(82,46,8,0.6)' }}>
            {idx + 1} / {total}
          </div>
        </div>

        {/* Image frame — taller passage area means the image now uses a
            wider (16:10) crop and a hard maxHeight cap, instead of 4:3.
            We render the photo twice — a blurred version as a backdrop
            fill (so the empty box area is never black) AND a sharp
            contained version on top (so the whole photo is visible). */}
        <div className="mt-3 rounded-md overflow-hidden relative"
          style={{
            border: `2px solid ${accent}`,
            boxShadow: `inset 0 0 0 2px rgba(255,241,184,0.5), 0 0 14px ${accent}55`,
            background: '#0a0608',
            aspectRatio: '16 / 10',
            maxHeight: '36vh',
            width: '100%',
            alignSelf: 'center',
          }}>
          {memory?.image_url ? (
            <>
              {/* Blurred backdrop — fills any letterbox area with the photo's own color */}
              <img src={memory.image_url} alt="" aria-hidden draggable={false}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  filter: 'blur(20px) brightness(0.75)',
                  transform: 'scale(1.15)',  /* hide blur edge artifacts */
                }}/>
              {/* Sharp foreground — fully contained, whole image visible */}
              <img src={memory.image_url} alt={memory.note || 'memory'} draggable={false}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'contain',
                }}/>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ color: '#a08a4a' }}>
              No image
            </div>
          )}
        </div>

        {/* Date / time stamp */}
        <div className="mt-2 text-center font-['Cormorant_Garamond'] italic text-[12px]"
          style={{ color: 'rgba(82,46,8,0.7)' }}>
          {dateStr}
        </div>

        {/* Original upload caption (readonly) — only shown if the user wrote
            something during upload. Sits between the date and the empty
            passage textarea below. */}
        {memory?.note && (
          <div
            className="mt-2 rounded-md px-3 py-2 font-['Cormorant_Garamond'] text-[13px] italic text-center"
            style={{
              background: 'rgba(82,46,8,0.08)',
              border: '1px dashed rgba(82,46,8,0.35)',
              color: 'rgba(60,38,8,0.85)',
            }}
          >
            “{memory.note}”
          </div>
        )}

        {/* Passage textarea — restored to its previous size so the paper
            page keeps the tall, roomy feel it had before.  If the total
            page content exceeds the paper's height, the outer column
            scrolls to reveal the SEAL PASSAGE button. */}
        <div className="mt-3 flex-1 flex flex-col">
          <div className="font-['Cormorant_Garamond'] uppercase text-[10px] tracking-[0.32em] mb-1"
            style={{ color: 'rgba(82,46,8,0.6)' }}>
            ~ Sovereign&apos;s Passage ~
          </div>
          <textarea
            data-testid="sovereigns-passage-input"
            value={draftNote}
            onChange={(e) => onChangeNote(e.target.value)}
            placeholder="Inscribe new reflections — what does this moment mean now?"
            rows={6}
            className="w-full flex-1 resize-none rounded-md p-3 font-['Cormorant_Garamond']"
            style={{
              background: 'rgba(255,247,220,0.7)',
              border: `1px solid ${accent}88`,
              color: INK,
              fontSize: 14,
              lineHeight: 1.5,
              outline: 'none',
              minHeight: 160,
            }}
          />
          <div className="flex justify-end mt-2">
            <button
              type="button"
              data-testid="sovereigns-save-passage"
              onClick={onSave}
              disabled={savingNote}
              className="px-4 py-1.5 rounded-md font-['Cormorant_Garamond'] uppercase text-[11px] tracking-[0.18em]"
              style={{
                background: `linear-gradient(180deg, ${accent} 0%, ${accent}88 100%)`,
                color: '#0a0604', border: '1px solid rgba(0,0,0,0.4)',
                fontWeight: 700, letterSpacing: '0.18em',
                boxShadow: '0 1px 0 rgba(255,255,255,0.35) inset, 0 4px 10px rgba(0,0,0,0.35)',
                opacity: savingNote ? 0.6 : 1,
              }}
            >
              {savingNote ? 'Sealing…' : 'Seal Passage'}
            </button>
          </div>
        </div>
      </div>
    </PaperPage>
  );
};

/* ── Empty-chapter page (when category has no images yet) ── */
const EmptyChapterPage = ({ chapter, onBackToIndex }) => (
  <PaperPage>
    <div className="relative flex-1 flex flex-col items-center justify-center px-6 text-center">
      <button type="button" onClick={onBackToIndex}
        data-testid="sovereigns-back-to-index"
        className="absolute top-4 left-4 font-['Cormorant_Garamond'] uppercase text-[10px] tracking-[0.28em] flex items-center gap-1"
        style={{ color: 'rgba(82,46,8,0.7)' }}>
        <ChevronLeft size={14} /> Cover
      </button>
      <img src={chapter.icon} alt="" draggable={false}
        style={{ width: 96, height: 96, objectFit: 'contain', opacity: 0.6,
          filter: `drop-shadow(0 0 12px ${chapter.accent})` }} />
      <div className="mt-5 font-['Cormorant_Garamond'] font-bold"
        style={{ color: chapter.accent, fontSize: 26, letterSpacing: '0.05em' }}>
        {chapter.label} Path
      </div>
      <p className="mt-3 font-['Cormorant_Garamond'] italic text-[14px]" style={{ color: 'rgba(82,46,8,0.7)' }}>
        Thy chronicle in this realm is yet unwritten.<br />
        Upload a moment to begin.
      </p>
    </div>
  </PaperPage>
);

/* ── Main book ── */
export const SovereignsPath = ({ onClose, pillarId = null }) => {
  const { user } = useAuth();
  /* Phase machine simplified (Feb 2026 per user request):
       'master' → cover of the volume
       'chapter' → chronological image pages (newest first)
     The old 'library' bookshelf and per-chapter 'cover' phases were
     removed — a single tap flips straight from cover into the pages. */
  const [phase, setPhase] = useState('master');
  const [chapterMemories, setChapterMemories] = useState([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [draftNotes, setDraftNotes] = useState({});
  const [savingNote, setSavingNote] = useState(false);
  const [flipDir, setFlipDir] = useState(1);

  const empireName = user?.nickname || user?.email?.split('@')[0] || 'Sovereign';
  const chapter = CHAPTERS.find((c) => c.id === pillarId) || null;

  /* Load memories — either filtered by pillar or ALL memories across
     categories.  Sorted newest → oldest by created_at so the freshest
     page appears first when the user turns the cover. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = pillarId
          ? `${BACKEND_URL}/api/memories?category=${pillarId}`
          : `${BACKEND_URL}/api/memories`;
        const { data } = await axios.get(url);
        const list = (Array.isArray(data) ? data : [])
          .filter((m) => m.image_url)
          .sort((a, b) => {
            const ta = new Date(a.created_at || 0).getTime();
            const tb = new Date(b.created_at || 0).getTime();
            return tb - ta;
          });
        if (cancelled) return;
        setChapterMemories(list);
        setPageIdx(0);
        const seed = {};
        list.forEach((m) => { seed[m.id] = m.passage || ''; });
        setDraftNotes(seed);
      } catch {
        if (!cancelled) setChapterMemories([]);
      }
    })();
    return () => { cancelled = true; };
  }, [pillarId]);

  /* Save current passage. */
  const saveNote = async () => {
    const mem = chapterMemories[pageIdx];
    if (!mem) return;
    const passage = draftNotes[mem.id] || '';
    setSavingNote(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/memories/${mem.id}`, { passage });
      setChapterMemories((prev) => prev.map((m) => m.id === mem.id ? { ...m, passage } : m));
    } catch {
      /* swallow */
    } finally {
      setSavingNote(false);
    }
  };

  /* ── Page navigation ── master ↔ chapter pages ── */
  const flipNext = () => {
    setFlipDir(1);
    if (phase === 'master') { setPhase('chapter'); return; }
    if (phase === 'chapter' && pageIdx < chapterMemories.length - 1) {
      setPageIdx(pageIdx + 1);
    }
  };
  const flipPrevImage = () => {
    setFlipDir(-1);
    if (phase === 'chapter' && pageIdx > 0) setPageIdx(pageIdx - 1);
  };
  const flipBackBottom = () => {
    setFlipDir(-1);
    if (phase === 'master') return;
    if (phase === 'chapter') {
      if (pageIdx > 0) { setPageIdx(pageIdx - 1); return; }
      setPhase('master');
      return;
    }
  };
  const goBackToCover = () => {
    setFlipDir(-1);
    setPhase('master');
    setPageIdx(0);
  };

  /* Header label */
  const headerLabel = useMemo(() => {
    if (phase === 'master')  return 'COVER';
    if (chapter) return `${chapter.label.toUpperCase()} PATH`;
    return 'PATH';
  }, [phase, chapter]);

  /* Effective pillar object used by ImagePage / PillarMasterCover — for
     the GLOBAL path (no pillarId), synthesise a gold-royal default.
     `label` carries the apostrophe so the paper header reads
     "SOVEREIGN'S PATH" (not "SOVEREIGN PATH"). */
  const effectivePillar = chapter || {
    id: 'global', label: 'Sovereign\u2019s',
    accent: GOLD, icon: '/icons/empire_seal.png', bg: '#1a0628',
  };

  /* Touch swipe — REAL BOOK gesture:
       swipe LEFT→RIGHT (dx>0) → PREVIOUS page (back toward cover)
       swipe RIGHT→LEFT (dx<0) → NEXT page (advance) */
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    const sx = touchStartX.current; const sy = touchStartY.current;
    touchStartX.current = null; touchStartY.current = null;
    if (sx == null) return;
    const dx = e.changedTouches[0].clientX - sx;
    const dy = e.changedTouches[0].clientY - (sy ?? 0);
    if (Math.abs(dx) < 60) return;
    if (Math.abs(dy) > Math.abs(dx)) return; // vertical scroll wins
    if (dx < 0) flipNext();
    else flipBackBottom();
  };

  /* Page node */
  let pageNode = null;
  if (phase === 'master') {
    /* Per-pillar volume gets the pillar-tinted cover; global volume keeps
       the royal purple master cover for backwards visual continuity. */
    pageNode = pillarId ? (
      <PillarMasterCover
        empireName={empireName}
        profilePicture={user?.profile_picture}
        pillar={effectivePillar}
      />
    ) : (
      <MasterCoverPage
        empireName={empireName}
        profilePicture={user?.profile_picture}
      />
    );
  } else if (phase === 'chapter') {
    if (chapterMemories.length === 0) {
      pageNode = <EmptyChapterPage chapter={effectivePillar} onBackToIndex={goBackToCover} />;
    } else {
      const mem = chapterMemories[pageIdx];
      pageNode = (
        <ImagePage
          chapter={effectivePillar}
          memory={mem}
          idx={pageIdx}
          total={chapterMemories.length}
          draftNote={draftNotes[mem?.id] || ''}
          onChangeNote={(v) => setDraftNotes((prev) => ({ ...prev, [mem.id]: v }))}
          onSave={saveNote}
          savingNote={savingNote}
          onBackToIndex={goBackToCover}
        />
      );
    }
  }

  /* Tap-zone helpers */
  const tapNext = (e) => {
    if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
    flipNext();
  };
  const tapPrev = (e) => {
    if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
    if (phase === 'chapter') flipPrevImage();
    else flipBackBottom();
  };

  return (
    <div className="fixed inset-0 z-[120] flex flex-col imperium-shell"
      style={{ background: 'radial-gradient(circle at 50% 30%, #1a0d04 0%, #050306 100%)' }}
      data-testid="sovereigns-path-modal">

      {/* Chroma-key SVG filter (unchanged) */}
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }} aria-hidden>
        <defs>
          <filter id="chromaPunchBlack" colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values="
              1 0 0 0 0
              0 1 0 0 0
              0 0 1 0 0
              1 1 1 0 0"/>
            <feComponentTransfer>
              <feFuncA type="table" tableValues="0 0 1 1"/>
            </feComponentTransfer>
          </filter>
        </defs>
      </svg>

      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: `1px solid ${GOLD}33` }}>
        <BackPill onClick={onClose} accent="#B57EDC" testId="sovereigns-close"/>
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2">
            <Feather size={16} style={{ color: GOLD }} />
            <div className="font-['Cormorant_Garamond'] text-[18px]" style={{ color: GOLD_LIGHT, letterSpacing: '0.04em' }}>
              {pillarId && chapter ? `${chapter.label} Path` : 'Sovereign\u2019s Path'}
            </div>
          </div>
          <p className="font-['Outfit'] text-[10px] tracking-[0.18em] text-white/30 mt-0.5" data-testid="sovereigns-page-label">
            {headerLabel}
          </p>
        </div>
        <div className="w-14" />
      </div>

      {/* Book area — expanded to fill space freed by removed bottom bar.
          Swipe RIGHT → next page, swipe LEFT → previous page (→ cover). */}
      <div className="flex-1 flex items-center justify-center px-2 pb-3 overflow-hidden"
        style={{ paddingBottom: 'calc(12px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="relative w-full max-w-[480px] h-full" style={{ maxHeight: 900 }}>
          <div onClick={tapPrev} className="absolute left-0 z-30"
            style={{ top: 40, bottom: 40, width: '28%', cursor: 'pointer' }} aria-hidden />
          <div onClick={tapNext} className="absolute right-0 z-30"
            style={{ top: 40, bottom: 40, width: '28%', cursor: 'pointer' }} aria-hidden />
          <AnimatePresence initial={false} custom={flipDir} mode="wait">
            <motion.div
              key={`${phase}-${pageIdx}`}
              custom={flipDir}
              initial={{ opacity: 0, rotateY: flipDir > 0 ? 170 : -170 }}
              animate={{ opacity: 1, rotateY: 0 }}
              exit={
                flipDir > 0
                  ? { rotateY: -170, opacity: 0 }
                  : { rotateY: 170,  opacity: 0 }
              }
              transition={{
                rotateY: { duration: 0.7, ease: [0.55, 0.05, 0.25, 0.98] },
                opacity: { duration: 0.55 },
              }}
              className="absolute inset-0"
              style={{
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
                perspective: 1600,
                transformOrigin: flipDir > 0 ? 'left center' : 'right center',
                boxShadow: '0 8px 28px rgba(0,0,0,0.6), inset 0 0 40px rgba(0,0,0,0.25)',
              }}
            >
              {pageNode}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <style>{`
        .no-scrollbar-book::-webkit-scrollbar { display: none; }
        .no-scrollbar-book { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default SovereignsPath;
